import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/context/LangContext";
import { Sparkles, MapPin, PiggyBank, Zap, ArrowRight, X } from "lucide-react";

const STORAGE_KEY = "bhealth_welcome_seen_v1";

export default function WelcomePopup({ disabled = false }) {
  const { t } = useT();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (disabled) return;
    if (typeof window === "undefined") return;
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const id = setTimeout(() => setVisible(true), 650);
        return () => clearTimeout(id);
      }
    } catch {
      // private mode etc.
    }
  }, []);

  // Lock body scroll while visible
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("pop.title_1")}
      data-testid="welcome-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200"
      onClick={dismiss}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative w-full max-w-2xl rounded-3xl bg-navy-900 text-white shadow-2xl max-h-[92vh] overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrollable inner */}
        <div className="relative max-h-[92vh] overflow-y-auto overscroll-contain">
          {/* Decorative glow */}
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] bg-lime-400/40 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-24 w-[300px] h-[300px] bg-lime-500/20 rounded-full blur-3xl pointer-events-none" />
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px), linear-gradient(to right, rgba(255,255,255,.8) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          />

          {/* Close button — sticky so always reachable on mobile */}
          <div className="sticky top-0 z-30 flex justify-end pt-3 pr-3">
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              data-testid="welcome-popup-close"
              className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md flex items-center justify-center transition shadow-lg"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="relative p-6 pt-2 sm:p-8 md:p-10 md:pt-4 z-10">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-lime-500 text-navy-900 px-3 py-1 text-[11px] uppercase tracking-widest font-black mb-4"
              data-testid="welcome-popup-kicker"
            >
              <Sparkles className="w-3.5 h-3.5" /> {t("pop.kicker")}
            </div>

            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              <span className="relative inline-block">
                <span className="relative z-10 italic">{t("pop.title_1")}</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-lime-400/70 -z-0" />
              </span>
              <span className="text-white/90">{t("pop.title_2")}</span>
            </h2>

            <p className="mt-4 text-white/75 text-sm sm:text-base md:text-lg leading-relaxed max-w-xl">
              {t("pop.lead")}
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Pillar icon={MapPin} title={t("pop.pillar_1.title")} desc={t("pop.pillar_1.desc")} />
              <Pillar icon={PiggyBank} title={t("pop.pillar_2.title")} desc={t("pop.pillar_2.desc")} />
              <Pillar icon={Zap} title={t("pop.pillar_3.title")} desc={t("pop.pillar_3.desc")} />
            </div>

            <div className="mt-6 rounded-2xl border border-lime-400/40 bg-lime-400/10 px-4 py-3">
              <p className="font-heading font-bold text-lime-300 text-sm md:text-base tracking-tight text-center">
                ⚡ {t("pop.punch")}
              </p>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/devenir-partenaire"
                onClick={dismiss}
                className="inline-flex items-center justify-center gap-2 bg-lime-500 hover:bg-lime-400 text-navy-900 font-bold px-6 py-3.5 rounded-full transition text-sm md:text-base shadow-lg shadow-lime-500/20 flex-1"
                data-testid="welcome-popup-cta"
              >
                {t("pop.cta")} <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                type="button"
                onClick={dismiss}
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold px-6 py-3.5 rounded-full transition text-sm md:text-base"
                data-testid="welcome-popup-dismiss"
              >
                {t("pop.dismiss")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pillar({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-4">
      <div className="w-9 h-9 rounded-lg bg-lime-400/20 flex items-center justify-center mb-2">
        <Icon className="w-4 h-4 text-lime-300" />
      </div>
      <div className="font-heading font-bold text-white text-sm leading-tight mb-1">{title}</div>
      <p className="text-xs text-white/60 leading-snug">{desc}</p>
    </div>
  );
}
